// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Select, { SelectPresentationProps } from "@sc/MobiusOverrides/Select/Select";
import TextField, { TextFieldProps } from "@sc/MobiusOverrides/TextField/TextField";
import { SHOW_ALL_BRANDS_KEY } from "@sc/XDL/constants";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
    // XNG-Changes
    handleSearchFilterBrands?: (e: string) => void;
    searchFilterBrands?: string;
    lastLetterClicked?: string;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationLinkText?: TypographyPresentationProps;
    navigationTextEmpty?: TypographyPresentationProps;
    // XNG-Changes
    alphabetContainer?: InjectableCss;
    titleText?: TypographyPresentationProps;
    brandsLettersWrapper?: InjectableCss;
    alphabetSection?: InjectableCss;
    filterSection?: InjectableCss;
    filterInput?: TextFieldProps;
    selectWrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            text-transform: uppercase;
            justify-items: center;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
        `,
    },
    navigationItem: {
        css: css`
            margin: 0;
            display: inline-block;
            flex: 0 0 30px;
        `,
    },
    navigationLink: {
        css: css`
            display: block;
        `,
        typographyProps: {
            transform: "uppercase",
            variant: "headerTertiary",
            forwardAs: "span",
        },
    },
    navigationTextEmpty: {
        transform: "uppercase",
        variant: "headerTertiary",
        forwardAs: "span",
        color: "text.disabled",
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));
    // xng-change
    const [alphabet] = useState(() => [{ letter: SHOW_ALL_BRANDS_KEY, count: 0, linkable: true }, ...props.alphabet]);

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };

    // XNG-Changes
    const createHandleClickOfLetter = (letter: string) => () => {
        // const alphabetElement = document.getElementById("alphabetNavigation");
        // const letterElement = document.getElementById(`${props.prefix || ""}letter-${letter}`);
        // if (alphabetElement && letterElement) {
        //     const alphabetOffset = alphabetElement.offsetTop;
        //     const letterOffset = letterElement.offsetTop;
        //     if (letterOffset - alphabetOffset > 0) {
        //         window.scrollTo(0, letterOffset - alphabetOffset);
        //     }
        // }
        if (props.searchFilterBrands && props.handleSearchFilterBrands) {
            props.handleSearchFilterBrands(props.searchFilterBrands);
        }
        return handleClickOfLetter(letter);
    };

    // XNG-Changes
    if (alphabet.length <= 1) {
        return null;
    }

    // xng-change-structure
    return (
        <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation" id="alphabetNavigation">
            <StyledWrapper {...styles.brandsLettersWrapper}>
                <StyledWrapper {...styles.alphabetSection}>
                    <Typography {...styles.titleText}>{translate("Filter Alphabetically")}</Typography>
                    <StyledWrapper {...styles.alphabetContainer}>
                        {alphabet.map(alphabetItem => (
                            <StyledSpan
                                {...styles.navigationItem}
                                key={alphabetItem.letter}
                                data-test-selector="alphabetNavigationItemLetter"
                            >
                                {alphabetItem.linkable ? (
                                    <Link
                                        {...styles.navigationLink}
                                        onClick={createHandleClickOfLetter(alphabetItem.letter)}
                                        {...{
                                            lastLetterClicked:
                                                props.lastLetterClicked === alphabetItem.letter &&
                                                alphabetItem.linkable,
                                        }}
                                    >
                                        {alphabetItem.letter === "#" ? translate("0-9") : alphabetItem.letter}
                                    </Link>
                                ) : (
                                    <Typography {...styles.navigationTextEmpty}>{alphabetItem.letter}</Typography>
                                )}
                            </StyledSpan>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.selectWrapper}>
                    <Select
                        {...styles.select}
                        placeholder={translate("Search by First Letter")}
                        onChange={e => handleClickOfLetter(e.target.value)}
                        value={props.lastLetterClicked}
                        label={translate("Filter Alphabetically")}
                    >
                        {alphabet.map(alphabetItem => (
                            <option
                                disabled={!alphabetItem.linkable}
                                key={alphabetItem.letter}
                                value={alphabetItem.letter}
                            >
                                {alphabetItem.letter === "#"
                                    ? translate("0-9")
                                    : alphabetItem.letter === SHOW_ALL_BRANDS_KEY
                                    ? translate("Show All Brands")
                                    : alphabetItem.letter.toUpperCase()}
                            </option>
                        ))}
                    </Select>
                </StyledWrapper>
                <StyledWrapper {...styles.filterSection}>
                    <TextField
                        name="filterBrands"
                        value={props.searchFilterBrands}
                        placeholder={translate("Search by Name")}
                        onChange={e => props.handleSearchFilterBrands?.(e.target.value)}
                        label={translate("Search by Name")}
                        {...styles.filterInput}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
